import React from 'react';
import { HiOutlineArrowRight } from 'react-icons/hi2';
import styles from './Projects.module.css';

const ProjectCard = ({ 
  project, 
  onClick, 
  onUpdateStatus, 
  isUpdating,
  navigate 
}) => {
  const formatCurrency = (amount) => {
    if (!amount) return '£0.00';
    return `£${(amount / 100).toFixed(2)}`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    if (timestamp.toDate) {
      return timestamp.toDate().toLocaleDateString();
    }
    return new Date(timestamp).toLocaleDateString();
  };

  const formatRingPrice = (price) => {
    if (price === null || price === undefined || price === '') return 'Price not specified';
    if (typeof price === 'number') return `£${price.toLocaleString()}`;
    const priceString = price.toString().trim();
    if (!priceString) return 'Price not specified';
    const cleaned = priceString.replace(/[£,]/g, '');
    if (cleaned && !isNaN(cleaned)) {
      return `£${Number(cleaned).toLocaleString()}`;
    }
    return priceString.startsWith('£') ? priceString : `£${priceString}`;
  };

  const featuredRing = project.enquiryDetails?.featuredRing || null;
  const ringDetailsList = Array.isArray(project.enquiryDetails?.ringDetails)
    ? project.enquiryDetails.ringDetails
    : [];
  const fallbackRing = ringDetailsList[0] || null;
  const ringSummary = featuredRing || fallbackRing;
  const ringName =
    featuredRing?.name ||
    fallbackRing?.title ||
    fallbackRing?.name ||
    'Ring reference';
  const ringImageUrl = featuredRing?.imageUrl || fallbackRing?.imageUrl || '';
  const ringPrice = featuredRing?.price ?? fallbackRing?.price ?? null;
  const ringAvailability = featuredRing?.availablePurchase ?? fallbackRing?.availablePurchase;

  return (
    <div className={styles.projectCard} onClick={onClick}>
      <div className={styles.projectCardHeader}>
        <div className={styles.projectCardTitle}>
          <h3 className={styles.projectName}>
            {project.projectName || project.description || 'Untitled Project'}
          </h3>
        </div>
        <span className={`${styles.statusBadge} ${
          project.status === 'project_started' 
            ? styles.statusBadgeInProgress 
            : styles.statusBadgeCompleted
        }`}>
          {project.status === 'project_started' ? 'In Progress' : 'Completed'}
        </span>
      </div>

      <div className={styles.projectCardBody}>
        <div className={styles.projectCardCustomer}>
          <div className={styles.customerName}>
            {project.customer?.name || 'Unknown Customer'}
          </div>
          {project.customer?.email && (
            <div className={styles.customerEmail}>
              {project.customer.email}
            </div>
          )}
        </div>

        {ringSummary && (
          <div className={styles.ringSummary}>
            <div className={styles.ringSummaryLabel}>Ring referenced</div>
            <div className={styles.ringSummaryContent}>
              <div className={styles.ringSummaryImage}>
                {ringImageUrl ? (
                  <img
                    src={ringImageUrl}
                    alt={ringName}
                    className={styles.ringSummaryImageTag}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className={styles.ringSummaryPlaceholder}>No image</div>
                )}
              </div>
              <div className={styles.ringSummaryDetails}>
                <div className={styles.ringSummaryTitleRow}>
                  <div className={styles.ringSummaryName}>{ringName}</div>
                  {typeof ringAvailability === 'boolean' && (
                    <span
                      className={`${styles.ringSummaryBadge} ${
                        ringAvailability ? styles.ringSummaryBadgeAvailable : styles.ringSummaryBadgeUnavailable
                      }`}
                    >
                      {ringAvailability ? 'Available to order' : 'Previous commission'}
                    </span>
                  )}
                </div>
                <div className={styles.ringSummaryMeta}>{formatRingPrice(ringPrice)}</div>
              </div>
            </div>
          </div>
        )}

        <div className={styles.projectCardInfo}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Total Invoiced</span>
            <span className={styles.infoValue}>
              {formatCurrency(project.totalInvoiced)}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Total Paid</span>
            <span className={styles.infoValue}>
              {formatCurrency(project.totalPaid || 0)}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Started</span>
            <span className={styles.infoValue}>
              {formatDate(project.stage?.projectStartedDate || project.createdAt)}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Timeline</span>
            <span className={styles.infoValue}>
              {project.enquiryDetails?.timeline || 'Not specified'}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.projectCardActions}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap' }}
        >
          More Details
          <HiOutlineArrowRight style={{ marginLeft: 'var(--spacing-xs)', flexShrink: 0 }} />
        </button>
      </div>
    </div>
  );
};

export default ProjectCard;
